import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import * as AppConstants from '../../config/AppConstants'
import { useAuthContext } from '../../auth/AuthContext';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import Input from '../../components/basic/Input';
import Button from '../../components/basic/Button';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useGlobalContext } from '../../state/GlobalContext';
import useInterval from '@rooks/use-interval';
import api from '../../client/api';
import { useMediaQuery } from 'react-responsive';
import { IconsLibrary } from "../../util/FontAwesomeIconsUtil";
import SelectHeader from '../../components/basic/SelectHeader';
import { useToastContext } from '../../state/ToastContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import RobaSvg from '../../../assets/images/RobaSvg';

function Branding(props) {
    const { title, ...rest } = props;
    return (
        <NavLink to={AppConstants.HOME_URL} {...rest} title="Roba.ba logo">
            <RobaSvg className="min-w-24 w-24" />
        </NavLink>
    );
}

function PublishNavLink(props) {
    return (
        <NavLink className="whitespace-nowrap text-base font-semibold text-gray-darker rounded-md bg-yellow hover:bg-yellow-dark hover:text-gray-darkest hover:border-yellow-dark py-2 px-5 focus:outline-none focus:shadow-outline border-2 border-yellow transition" {...props} title="Dodaj oglas">
            <FontAwesomeIcon icon={IconsLibrary.faPlus} />
            {!!props.children && <span className="ml-3">{props.children}</span>}
        </NavLink>
    );
}

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

function SearchBar(props) {

    const location = useLocation();
    const query = useQuery();
    const { notify, setContextStyleColor } = useToastContext();
    const history = useHistory();
    const parts = location.pathname.split(/[/](.*)[/]/);
    const isProfile = location.pathname.substring(0, 8) === "/profil/" && !!parts[1] && parts[1] === "profil";
    const isCategory = !!parts[1] && parts[1].includes("/kategorija");
    const extractKeywords = query.get("keywords");
    const isMediumTablet = useMediaQuery({ query: '(max-width: 999px)' })
    const [keywordsForSearch, setKeywordsForSearch] = React.useState((!!isCategory || !!isProfile) ? null : !!extractKeywords ? extractKeywords : null);
    const [searchType, setSearchType] = React.useState(location.pathname === "/pretraga/korisnici" ? 'user' : 'listing');

    React.useEffect(() => {
        setContextStyleColor("bg-red");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const searchOptions = [
        {
            id: "listing",
            name: "Oglas"
        },
        {
            id: "user",
            name: "Korisnik"
        }
    ];

    const handleStateChange = event => {
        setSearchType(event.target.value);
    };

    const handleSearchPageRouting = (e) => {
        e.preventDefault();
        if (!!searchType && searchType === 'listing') {
            history.replace({ pathname: !!keywordsForSearch ? '/pretraga' : '/', search: !!keywordsForSearch ? '?keywords=' + keywordsForSearch : null });
        } else if (!!searchType && searchType === 'user') {
            history.replace({ pathname: !!keywordsForSearch ? '/pretraga/korisnici' : '/', search: !!keywordsForSearch ? '?keywords=' + keywordsForSearch : null });
        }
    };

    const handleInputSearchBarValueChange = (e) => {
        if (!!e.target.value) {
            setKeywordsForSearch(e.target.value);
        } else {
            setKeywordsForSearch('');
        }
    };

    const handleSearchPageRoutingInvalidKeywordSize = (e) => {
        e.preventDefault();
        notify({
            text: "Minimalan broj karaktera za pretragu je 3!",
            id: "min-chars-for-search-error",
          });
    };

    return (
        <form method="get" onSubmit={(e) => !!keywordsForSearch && keywordsForSearch.length > 0 && keywordsForSearch.length < 3 ? handleSearchPageRoutingInvalidKeywordSize(e) : handleSearchPageRouting(e)} className={`flex flex-nowrap direction-row flex-grow w-full ${!!isMediumTablet ? `px-0` : `px-10`}`}>
            <div className="flex flex-grow max-w-4xl mx-auto">
                <Input id="query" type="text" defaultValue={(!!isCategory || !!isProfile) ? "" : extractKeywords} placeholder="Ključne riječi" name="keywords" 
                onChange={handleInputSearchBarValueChange} additionalClasses="w-full rounded-r-none bg-gray-lightest"
                invalid={!!keywordsForSearch && keywordsForSearch.length > 0 && keywordsForSearch.length < 3 ? true : false} />
                <SelectHeader
                 variant="primary"
                 options={searchOptions}
                 value={searchType}
                 onChange={handleStateChange}
                 additionalClasses="bg-purple"
                />
                <Button variant="primary" additionalClasses="rounded-l-none text-xl" onClick={(e) => {
                    if (!!keywordsForSearch && keywordsForSearch.length > 0 && keywordsForSearch.length < 3) {
                        handleSearchPageRoutingInvalidKeywordSize(e)
                    } else {
                        handleSearchPageRouting(e);
                    }
                }}><FontAwesomeIcon icon={IconsLibrary.faSearch} />
                </Button>
            </div>
        </form>
    );
}

function DropdownNavButton(props) {
    const { icon, children } = props;
    const ref = React.useRef();
    const [open, setOpen] = React.useState(false);
    const isMobile = useMediaQuery({ query: '(max-width: 470px)' })

    useOnClickOutside(ref, () => setOpen(false));

    const buttonClasses = "py-2 px-4 " + (open ? " bg-purple text-white rounded-b-none" : " text-purple hover:bg-gray-lightest");

    return (
        <div className="relative" {...props} ref={ref}>
            <Button onClick={() => setOpen(!open)} additionalClasses={buttonClasses}>{icon}</Button>
            {!isMobile && !!open && (
                <div className="absolute left-0 py-1 w-64 bg-purple text-white rounded-md rounded-tl-none shadow" onClick={() => setOpen(!open)}>
                    {children}
                </div>
            )}
            {!!isMobile && !!open && (
                <div className="absolute right-0 py-1 w-64 bg-purple text-white rounded-md rounded-tr-none shadow" onClick={() => setOpen(!open)}>
                    {children}
                </div>
            )}
        </div>
    );
}

function DropdownNavItem({ title, icon, ...rest }) {
    return (
        <NavLink title={title} className="text-sm font-semibold tracking-wider block w-full px-4 py-1 leading-7 text-white transition hover:bg-purple-lighter hover:text-white" {...rest}>
            {!!icon && (
                <span className="flex flex-nowrap">
                    <span className="w-6 mr-1">{icon}</span>
                    <span>{title}</span>
                </span>
            )}
            {!icon && title}
        </NavLink>
    );
}

function DropdownNavItemButton({ title, icon, ...rest }) {
    return (
        <button type="button" className="text-sm font-semibold tracking-wider block w-full text-left px-4 py-2 text-white transition hover:bg-purple-lighter hover:text-white" {...rest}>
            {!!icon && (
                <span className="flex flex-nowrap">
                    <span className="w-6 mr-1">{icon}</span>
                    <span>{title}</span>
                </span>
            )}
            {!icon && title}
        </button>
    );
}

function DropdownNavDivider(props) {
    return (
        <hr className="my-0.5 border-purple-lighter" {...props} />
    );
}

function ButtonBadge({ value, additionalClasses, ...rest }) {

    const classes = "flex items-center justify-center py-1 px-2 -ml-1 -mb-4 text-xs font-bold leading-none text-purple bg-yellow rounded-full " + additionalClasses;

    return (
        <span className={classes} {...rest}>
            {value}
        </span>
    );
}

function HeaderTopLine({ auth, logout, currentUser }) {
    return (
        <div className="bg-gray-lightest text-gray-dark py-2 px-3 w-full text-sm">
            <div className="container flex justify-between items-center md:space-x-12 mx-auto">

                <nav className="flex justify-start space-x-6">
                    {/* social icons */}
                    <div className="flex flex-nowrap items-center justify-start space-x-4 text-lg leading-6 text-purple">
                        <a href="https://www.instagram.com/stiglaroba/" aria-label="Instagram" target="_blank" rel="noreferrer"><FontAwesomeIcon icon={IconsLibrary.fabInstagram} /></a>
                        <a href="https://www.facebook.com/stiglaroba.ba/" aria-label="Facebook" target="_blank" rel="noreferrer"><FontAwesomeIcon icon={IconsLibrary.fabfaFacebook} /></a>
                        <a href="https://www.youtube.com/channel/UC_Ycsu5Yk3Rb0LyZJf7geWw" aria-label="Youtube" target="_blank" rel="noreferrer"><FontAwesomeIcon icon={IconsLibrary.fabYoutube} /></a>
                        <a href="https://twitter.com/stiglaroba" aria-label="Twitter" target="_blank" rel="noreferrer"><FontAwesomeIcon icon={IconsLibrary.fabTwitter} /></a>
                        <a href="https://tiktok.com/@stiglaroba" aria-label="TikTok" target="_blank" rel="noreferrer"><FontAwesomeIcon icon={IconsLibrary.fabTiktok} /></a>
                    </div>
                </nav>

                {/* top line right navigation */}
                <nav className="flex justify-start space-x-6">

                    {/* show login and register links for unauthenticated users */}
                    {!auth.isAuthenticated && (
                        <>
                            {/* log in link */}
                            <NavLink to="/prijava" className="flex justify-start items-center space-x-2 hover:text-gray-darkest" title="Prijava">
                                <FontAwesomeIcon icon={IconsLibrary.faSignInAlt} className="text-purple" />
                                <span>Prijava</span>
                            </NavLink>

                            {/* register link */}
                            <NavLink to="/registracija" className="flex justify-start items-center space-x-2 hover:text-gray-darkest" title="Registracija">
                                <FontAwesomeIcon icon={IconsLibrary.faUserPlus} className="text-purple" />
                                <span>Registracija</span>
                            </NavLink>
                        </>
                    )}

                    {/* show logout and profile links */}
                    {!!auth.isAuthenticated && (
                        <>
                            {/* log in link */}
                            <button onClick={() => logout(auth.currentUser.email)} className="flex justify-start items-center space-x-2 hover:text-gray-darkest">
                                <FontAwesomeIcon icon={IconsLibrary.faSignOutAlt} className="text-purple" />
                                <span>Odjava</span>
                            </button>

                            {/* register link */}
                            <NavLink to="/kontrolna-tabla" className="flex justify-start items-center space-x-2 hover:text-gray-darkest" title="Kontrolna tabla">
                                <FontAwesomeIcon icon={IconsLibrary.farUser} className="text-purple" />
                                <span>Dobrodošli, <span className="font-semibold">{currentUser}</span></span>
                            </NavLink>
                        </>
                    )}
                </nav>
            </div>
        </div>
    );
}

function HeaderMainLine({ auth, logout, currentUser, messages, notifications, cartItems, isDesktopOrLaptop, isTablet, isSmallerTablet, page, isHomePageSliderShadow, isMobile }) {

    const { validateCartSyncUpdate, handleCartUpdate } = useGlobalContext();
    const [focused, setFocused] = React.useState(false);

    const handleStorageUpate = (e) => {
        const { key, newValue } = e;
        if (key === "shopping_cart") {
            if (!!newValue) {
                validateCartSyncUpdate(JSON.parse(newValue));
            } else {
                handleCartUpdate();
            }
        }
    }


    React.useEffect(() => {
        if (!focused) window.addEventListener("storage", handleStorageUpate);
        return () => {
            setFocused(false);
            if (!focused) window.removeEventListener('storage', handleStorageUpate)
          }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focused]);

    return (
        <div className={`bg-white sticky top-0 py-1.5 md:py-2.5 px-3 ${!!page && page === "home-page" && !!isHomePageSliderShadow ? `shadow` : ``} z-30`}>
            <div className="container flex justify-between items-center mx-auto">
                <div className="flex flex-grow justify-between items-center">
                    {/* branding component */}
                    <Branding title="Roba.ba" className="mr-4 w-24" />

                    {/* account icon buttons */}
                    <div className="flex justify-between items-center space-x-0.5 sm:space-x-1.5 md:space-x-1.5 lg:space-x-1.5">
                    {!!isMobile && !auth.isAuthenticated && (
                        <nav className="flex justify-start space-x-6">
                        {/* social icons */}
                        <div className="flex flex-nowrap items-center justify-start space-x-3.5 text-lg leading-6 text-purple">
                            <a href="https://www.facebook.com/stiglaroba.ba/" aria-label="Facebook"><FontAwesomeIcon icon={IconsLibrary.fabfaFacebook} /></a>
                            <a href="https://www.instagram.com/stiglaroba/" aria-label="Instagram"><FontAwesomeIcon icon={IconsLibrary.fabInstagram} /></a>
                            <a href="https://www.youtube.com/channel/UC_Ycsu5Yk3Rb0LyZJf7geWw" aria-label="Youtube"><FontAwesomeIcon icon={IconsLibrary.fabYoutube} /></a>
                            <a href="https://twitter.com/stiglaroba" aria-label="Twitter" rel="noreferrer"><FontAwesomeIcon icon={IconsLibrary.fabTwitter} /></a>
                            <a href="https://tiktok.com/@stiglaroba" aria-label="TikTok" rel="noreferrer"><FontAwesomeIcon icon={IconsLibrary.fabTiktok} /></a>
                        </div>
                    </nav>
                    )}
                    {!!isSmallerTablet && !auth.isAuthenticated && (
                        <nav className="flex justify-start space-x-6">
                        {/* social icons */}
                        <div className="flex flex-nowrap items-center justify-start space-x-4 text-lg leading-6 text-purple">
                            <a href="https://www.facebook.com/stiglaroba.ba/" aria-label="Facebook"><FontAwesomeIcon icon={IconsLibrary.fabfaFacebook} /></a>
                            <a href="https://www.instagram.com/stiglaroba/" aria-label="Instagram"><FontAwesomeIcon icon={IconsLibrary.fabInstagram} /></a>
                            <a href="https://www.youtube.com/channel/UC_Ycsu5Yk3Rb0LyZJf7geWw" aria-label="Youtube"><FontAwesomeIcon icon={IconsLibrary.fabYoutube} /></a>
                            <a href="https://twitter.com/stiglaroba" aria-label="Twitter" rel="noreferrer"><FontAwesomeIcon icon={IconsLibrary.fabTwitter} /></a>
                            <a href="https://tiktok.com/@stiglaroba" aria-label="TikTok" rel="noreferrer"><FontAwesomeIcon icon={IconsLibrary.fabTiktok} /></a>
                        </div>
                    </nav>
                    )}
                        {/* my account menu */}
                        {!isMobile && <DropdownNavButton title={currentUser} icon={<FontAwesomeIcon icon={IconsLibrary.faBars} />}>

                            {/* authenticated users dropdown */}
                            {!!auth.isAuthenticated && (
                                <>
                                    <DropdownNavItem title="Dodaj oglas" icon={<FontAwesomeIcon icon={IconsLibrary.faPlus} />} to={AppConstants.NEW_LISTING_URL} />
                                    <DropdownNavItem title="Kontrolna tabla" icon={<FontAwesomeIcon icon={IconsLibrary.faThLarge} />} to={AppConstants.DASHBOARD_URL} />

                                    <DropdownNavDivider />

                                    <DropdownNavItem title="Moji oglasi" icon={<FontAwesomeIcon icon={IconsLibrary.faList} />} to={AppConstants.MY_LISTINGS_URL} />
                                    <DropdownNavItem title="Moji favoriti" icon={<FontAwesomeIcon icon={IconsLibrary.faHeart} />} to={AppConstants.MY_FAVORITES_URL} />
                                    <DropdownNavItem title="Moje pretrage" icon={<FontAwesomeIcon icon={IconsLibrary.faSearch} />} to={AppConstants.MY_SEARCHES_URL} />

                                    <DropdownNavDivider />

                                    <DropdownNavItem title="Prodaje" icon={<FontAwesomeIcon icon={IconsLibrary.faStoreAlt} />} to={AppConstants.ORDERS_URL} />
                                    <DropdownNavItem title="Kupovine" icon={<FontAwesomeIcon icon={IconsLibrary.faShoppingCart} />} to={AppConstants.PURCHASES_URL} />
                                    {/* {!!auth.currentUser.companyAccount && <DropdownNavItem title="Paketi" icon={<FontAwesomeIcon icon={IconsLibrary.faBox} />} to={AppConstants.PLANS_URL} />} */}
                                    <DropdownNavItem title="Tokeni" icon={<FontAwesomeIcon icon={IconsLibrary.faCoins} />} to={AppConstants.TOKENS_URL} />
                                    <DropdownNavItem title="Uplate" icon={<FontAwesomeIcon icon={IconsLibrary.faWallet} />} to={AppConstants.FINANCE_URL} />

                                    <DropdownNavDivider />

                                    <DropdownNavItem title="Moj nalog" icon={<FontAwesomeIcon icon={IconsLibrary.faUser} />} to={AppConstants.MY_ACCOUNT_URL} />
                                    <DropdownNavItem title="Podešavanja" icon={<FontAwesomeIcon icon={IconsLibrary.faCog} />} to={AppConstants.SETTINGS_URL} />

                                    <DropdownNavDivider />

                                    <DropdownNavItemButton title="Odjava" icon={<FontAwesomeIcon icon={IconsLibrary.faSignOutAlt} />} onClick={logout} />
                                </>
                            )}

                            {/* unauthenticated users dropdown */}
                            {!auth.isAuthenticated && (
                                <>
                                    <DropdownNavItem title="Prijava" icon={<FontAwesomeIcon icon={IconsLibrary.faSignInAlt} />} to={AppConstants.LOGIN_URL} />
                                    <DropdownNavItem title="Registracija" icon={<FontAwesomeIcon icon={IconsLibrary.faUserPlus} />} to={AppConstants.REGISTER_URL} />
                                </>
                            )}

                        </DropdownNavButton>}

                        {!!auth.isAuthenticated && (
                            <>
                                {/* notifications */}
                                <NavLink
                                    to={AppConstants.NOTIFICATIONS_URL}
                                    title="Obavještenja"
                                    className={"flex flex-nowrap items-center transition rounded hover:bg-gray-lightest py-3 pl-3 " + (notifications ? "pr-1" : "pr-3")}
                                >
                                    <FontAwesomeIcon icon={IconsLibrary.faBell} className="text-purple" />
                                    {!!notifications && <ButtonBadge value={notifications} />}
                                </NavLink>

                                {/* messages */}
                                <NavLink
                                    to={AppConstants.MESSAGES_URL}
                                    title="Poruke"
                                    className={"flex flex-nowrap items-center transition rounded hover:bg-gray-lightest py-3 pl-3 " + (messages ? "pr-1" : "pr-3")}
                                >
                                    <FontAwesomeIcon icon={IconsLibrary.faEnvelope} className="text-purple" />
                                    {!!messages && <ButtonBadge value={messages > 99 ? "99+" : messages} />}
                                </NavLink>

                                {/* cart items */}
                                {!!isMobile && <NavLink to={AppConstants.CART_URL} className={"flex flex-nowrap items-center hover:bg-gray-lightest rounded transition py-3 pl-3 " + ((cartItems) ? "pr-1" : "pr-3")} title="Korpa">
                                    <FontAwesomeIcon icon={IconsLibrary.faShoppingCart} className="text-purple" />
                                    {!!cartItems && <ButtonBadge value={cartItems > 99 ? "99+" : cartItems} />}
                                </NavLink>}
                            </>
                        )}

                        {!!isMobile && <DropdownNavButton title={currentUser} icon={<FontAwesomeIcon icon={IconsLibrary.faBars} />}>

                            {/* authenticated users dropdown */}
                            {!!auth.isAuthenticated && (
                                <>
                                    <DropdownNavItem title="Dodaj oglas" icon={<FontAwesomeIcon icon={IconsLibrary.faPlus} />} to={AppConstants.NEW_LISTING_URL} />
                                    <DropdownNavItem title="Kontrolna tabla" icon={<FontAwesomeIcon icon={IconsLibrary.faThLarge} />} to={AppConstants.DASHBOARD_URL} />

                                    <DropdownNavDivider />

                                    <DropdownNavItem title="Moji oglasi" icon={<FontAwesomeIcon icon={IconsLibrary.faList} />} to={AppConstants.MY_LISTINGS_URL} />
                                    <DropdownNavItem title="Moji favoriti" icon={<FontAwesomeIcon icon={IconsLibrary.faHeart} />} to={AppConstants.MY_FAVORITES_URL} />
                                    <DropdownNavItem title="Moje pretrage" icon={<FontAwesomeIcon icon={IconsLibrary.faSearch} />} to={AppConstants.MY_SEARCHES_URL} />

                                    <DropdownNavDivider />

                                    <DropdownNavItem title="Prodaje" icon={<FontAwesomeIcon icon={IconsLibrary.faStoreAlt} />} to={AppConstants.ORDERS_URL} />
                                    <DropdownNavItem title="Kupovine" icon={<FontAwesomeIcon icon={IconsLibrary.faShoppingCart} />} to={AppConstants.PURCHASES_URL} />
                                    {/* {!!auth.currentUser.companyAccount && <DropdownNavItem title="Paketi" icon={<FontAwesomeIcon icon={IconsLibrary.faBox} />} to={AppConstants.PLANS_URL} />} */}
                                    <DropdownNavItem title="Tokeni" icon={<FontAwesomeIcon icon={IconsLibrary.faCoins} />} to={AppConstants.TOKENS_URL} />
                                    <DropdownNavItem title="Uplate" icon={<FontAwesomeIcon icon={IconsLibrary.faWallet} />} to={AppConstants.FINANCE_URL} />

                                    <DropdownNavDivider />

                                    <DropdownNavItem title="Moj nalog" icon={<FontAwesomeIcon icon={IconsLibrary.faUser} />} to={AppConstants.MY_ACCOUNT_URL} />
                                    <DropdownNavItem title="Podešavanja" icon={<FontAwesomeIcon icon={IconsLibrary.faCog} />} to={AppConstants.SETTINGS_URL} />

                                    <DropdownNavDivider />

                                    <DropdownNavItemButton title="Odjava" icon={<FontAwesomeIcon icon={IconsLibrary.faSignOutAlt} />} onClick={logout} />
                                </>
                            )}

                            {/* unauthenticated users dropdown */}
                            {!auth.isAuthenticated && (
                                <>
                                    <DropdownNavItem title="Prijava" icon={<FontAwesomeIcon icon={IconsLibrary.faSignInAlt} />} to={AppConstants.LOGIN_URL} />
                                    <DropdownNavItem title="Registracija" icon={<FontAwesomeIcon icon={IconsLibrary.faUserPlus} />} to={AppConstants.REGISTER_URL} />
                                </>
                            )}

                        </DropdownNavButton>}
                    </div>
                </div>

                {/* search bar component */}
                {!!isDesktopOrLaptop && <SearchBar />}

                <div className="flex justify-end items-center space-x-2">

                    {/* cart items */}
                    {!isMobile && <NavLink to={AppConstants.CART_URL} className={"flex flex-nowrap items-center hover:bg-gray-lightest rounded transition py-3 pl-3 " + ((cartItems) ? "pr-1" : "pr-3")} title="Korpa">
                        <FontAwesomeIcon icon={IconsLibrary.faShoppingCart} className="text-purple" />
                        {!!cartItems && <ButtonBadge value={cartItems > 99 ? "99+" : cartItems} />}
                    </NavLink>}

                    {/* publish listing button */}
                    {!isMobile && <PublishNavLink to={AppConstants.NEW_LISTING_URL}>{!!isDesktopOrLaptop && "Dodaj oglas"}</PublishNavLink>}

                </div>
            </div>
            {!isDesktopOrLaptop && <div className="container mx-auto pt-1">
                <SearchBar />
            </div>}
        </div>
    );
}

function Header({ page }) {

    const isDesktopOrLaptop = useMediaQuery({ query: '(min-width: 1000px)' })
    const isTablet = useMediaQuery({ query: '(min-width: 680px and max-width: 1200px)' })
    const isMobile = useMediaQuery({ query: '(max-width: 450px)' })
    const isSmallerTablet = useMediaQuery({ query: '(min-width: 451px) and (max-width: 999px)' });
    const isHomePageSliderShadow = useMediaQuery({ query: '(min-width: 981px)' });

    const { auth, logout } = useAuthContext();
    const { shoppingCart, handleCartUpdate } = useGlobalContext();

    const [messageCount, setMessageCount] = React.useState(0);
    const [notificationCount, setNotificationCount] = React.useState(0);

    const currentUser = (!!auth.currentUser && !!auth.currentUser.login) ? auth.currentUser.login : 'anonymous';

    const cartItems = !!shoppingCart.totalItems ? shoppingCart.totalItems : 0;

    React.useEffect(() => {
        if (!localStorage.getItem("cart_buyer_initiate")) {
            handleCartUpdate();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isMounted = React.useRef(true);

    useInterval(() => {
        if (auth.isAuthenticated) {
            api
                .get("/conversations/unread")
                .then(response => response.data)
                .then(response => {
                    if (isMounted.current) {
                        setMessageCount(response.data.unreadCount);
                    }
                })
                .catch(error => {/* ignore */ })
        }
    }, 30000, true);

    useInterval(() => {
        if (auth.isAuthenticated) {
            api
                .get("/notifications/unread")
                .then(response => response.data)
                .then(response => {
                    if (isMounted.current) {
                        setNotificationCount(response.data.count);
                    }
                })
                .catch(error => {/* ignore */ })
        }
    }, 20000, true);


    const handleUserLogOut = (userEmail) => {
        logout(userEmail);
    }

    return (
        <React.Fragment>
            {/* top line region */}
            {(!!isTablet || !!isDesktopOrLaptop) && <HeaderTopLine
                auth={auth}
                logout={handleUserLogOut}
                currentUser={currentUser}
            />}

            {/* main header region */}
            <HeaderMainLine
                isDesktopOrLaptop={isDesktopOrLaptop}
                isTablet={isTablet}
                isMobile={isMobile}
                isSmallerTablet={isSmallerTablet}
                isHomePageSliderShadow={isHomePageSliderShadow}
                auth={auth}
                page={page}
                logout={logout}
                currentUser={currentUser}
                messages={messageCount}
                notifications={notificationCount}
                cartItems={cartItems}
            />
        </React.Fragment >
    );
}

export default Header;